import React, {useCallback, useMemo} from 'react';

import {createListCollection, Input, Portal, Select, Switch, VStack} from "@chakra-ui/react";

import {Field, Button} from '@/components';
import {useForm} from "@/uses/useForm"
import stringUtil from "@/utils/stringUtil";
import ModalHeader from "@/components/entity/ModalHeader";
import {useLanguage} from "@/contexts/languageContext";

export const ConditionalForm = ({fields, type, onSubmit, close}) => {

    const {t, language} = useLanguage();
    const {formData, formFieldErrors, handleInputChange, handleInputChanges, handleFormSubmit} = useForm(
        {
            fieldId: '',
            type: 'nonEmpty',
            values: [],
        },
        {
            fieldId: {
                required: {
                    value: true,
                    message: t('errors.pleaseFillField')
                }
            },
            type: {
                required: {
                    value: true,
                    message: t('errors.pleaseFillField')
                }
            },
        }
    );

    const currentField = useCallback((_id) => {
        return fields[language].find(({id}) => id === _id)
    }, [fields, language]);

    const canChangeType = useCallback((id) => {
        return !Boolean(currentField(id)?.required)
    }, [currentField])

    const PossibleValues = useMemo(() => {
        switch (currentField(formData.fieldId)?.type) {
            case 'select':
            case 'checkbox':
            case 'radio':
                const collection = createListCollection({
                    items: currentField(formData.fieldId).choices.map((e) => ({label: e, value: e}))
                })

                return <Field
                    withBox={false}
                    flex={1}
                    label={t('wrappers.nonMandatoryLabel', { value: t('misc.valueToMakeFieldVisible') })}
                    errors={formFieldErrors.values}
                >
                    <Select.Root
                        multiple
                        collection={collection}
                        value={formData.values}
                        onValueChange={({value}) => {
                            handleInputChange('values', value)
                        }}
                    >
                        <Select.HiddenSelect />
                        <Select.Label>{ t('sentences.chooseOneOrMoreValues') }</Select.Label>
                        <Select.Control>
                            <Select.Trigger>
                                <Select.ValueText placeholder={t('sentences.chooseOneOrMoreValues')} />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                                <Select.Indicator />
                            </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                            <Select.Positioner>
                                <Select.Content>
                                    {collection.items.map((item) => (
                                        <Select.Item item={item} key={item.value}>
                                            {item.label}
                                            <Select.ItemIndicator />
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select.Positioner>
                        </Portal>
                    </Select.Root>
                </Field>
            case 'number':
            case 'text':
                return <Field
                    withBox={false}
                    flex={1}
                    label={t('wrappers.nonMandatoryLabel', { value: `${t('misc.valueToMakeFieldVisible')} (${t('misc.separateValuesBy')})` })}
                    errors={formFieldErrors.values}
                >
                    <Input
                        value={formData.values.join('|')}
                        onChange={e => handleInputChange('values', (e?.target?.value ?? '')
                            .split('|')
                            .map(e => currentField(formData.fieldId).type === 'number' ? parseFloat(e) : e))
                        }
                        placeholder={t('misc.fillWithValues')}
                    />
                </Field>;
            default:
                return null;
        }
    }, [formData]);

    const collection = useMemo(() => {
        return createListCollection({
            items: fields[language].map((e) => ({
                label: `${e?.label} ${e?.type}`,
                value: e?.id ?? stringUtil.generateAlphanumericHash(8)
            }))
        })
    }, [fields, language])

    return (
        <VStack w="100%" p="1.5rem">
            <ModalHeader title={t('misc.displayingConditions')} description={""} close={close}/>
            <VStack height="50vh" w="100%" flex={1} overflowY="hidden" alignItems="flex-start" gap="2vh" p={4}>
                {
                    <Field
                        label={t('wrappers.mandatoryLabel', { value: t('misc.conditionalToField') })}
                        p={4}
                    >
                        <Select.Root
                            collection={collection}
                            value={formData.fieldId?.[0] ? [formData.fieldId?.[0]] : []}
                            onValueChange={e => {
                                handleInputChanges({
                                    fieldId: e?.value?.[0] ?? '',
                                    type: canChangeType(e?.value?.[0] ?? '') ? 'nonEmpty' : 'values',
                                    values: []
                                });
                            }}
                        >
                            <Select.HiddenSelect/>
                            <Select.Control>
                                <Select.Trigger>
                                    <Select.ValueText placeholder={t('sentences.chooseFieldType')}/>
                                </Select.Trigger>
                                <Select.IndicatorGroup>
                                    <Select.Indicator/>
                                </Select.IndicatorGroup>
                            </Select.Control>
                            <Portal>
                                <Select.Positioner>
                                    <Select.Content>
                                        {collection.items.map((item) => (
                                            <Select.Item item={item} key={`select-item-${item.value}`}>
                                                {item.label}
                                                <Select.ItemIndicator/>
                                            </Select.Item>
                                        ))}
                                    </Select.Content>
                                </Select.Positioner>
                            </Portal>
                        </Select.Root>
                    </Field>
                }
                {
                    formData?.fieldId &&
                    <Field
                        label={t('wrappers.nonMandatoryLabel', { value: formData.type === 'values' ? t('sentences.conditionalToValues') : t('misc.fieldNotEmpty') })}
                        p={4}
                        isDisabled={!canChangeType(formData.fieldId)}
                        display="flex"
                        alignItems="center"
                    >
                        <Switch.Root
                            size="lg"
                            id={`required-field-${type}`}
                            checked={formData.type === 'values'}
                            onCheckedChange={(e) => handleInputChange('type', Boolean(e?.checked) ? 'values' : 'nonEmpty')}
                        >
                            <Switch.HiddenInput/>
                            <Switch.Control/>
                            <Switch.Label/>
                        </Switch.Root>
                    </Field>
                }
                {
                    formData.type === 'values' && <VStack flex={1} w="100%" overflowY="overlay">{PossibleValues}</VStack>
                }
                <Button
                    variant="primary"
                    alignSelf="center"
                    mt="auto"
                    height="auto"
                    borderRadius=".5rem"
                    style={{padding: "1rem 1.5rem"}}
                    onClick={() => handleFormSubmit(async (data) => {
                        await onSubmit(data);
                        return true;
                    }, close)}
                >
                    {
                        formData.fieldId !== null && formData.type ? t('actions.validate') : t('actions.cancel')
                    }
                </Button>
            </VStack>
        </VStack>
    );
};

export default ConditionalForm;
